/**
 * jrs-image-zoom.js — shared pinch/double-tap/wheel zoom for modal images.
 *
 * One gesture engine for every enlarged-image surface on the PDP:
 *   - product gallery fullscreen lightbox (product-gallery.js)
 *   - bundle colour image modal (betterconversions-bundle.js)
 *   - multistep PDP colour preview modal (pdp-multistep-form-v2.js)
 *
 * JRS.ImageZoom.attach(container, img, opts) -> handle
 *   container : element that receives gestures (should visually bound the image)
 *   img       : the <img> that gets transformed
 *   opts:
 *     maxScale       (default 4)    pinch/wheel ceiling
 *     doubleTapScale (default 2.5)  double-tap / double-click target
 *     clip           (default true) set overflow:hidden on the container.
 *                                   Pass false when chrome (e.g. a close
 *                                   button) overhangs the container box and
 *                                   must not be clipped — the zoomed image
 *                                   then spills over the backdrop instead,
 *                                   still bounded by the pan clamp.
 *     hint           (string|null)  one-line hint pill shown until first zoom
 *     onZoom(type, scale)           fired once per gesture type per attach when
 *                                   zoom first crosses 1x ('pinch'|'double_tap'|
 *                                   'double_click'|'wheel') — analytics hook
 *     onSwipe(direction)            horizontal swipe at 1x ('left'|'right') —
 *                                   lets the lightbox keep slide navigation
 *   handle.reset() / handle.refresh() / handle.destroy() / handle.isZoomed()
 *
 * Gestures while zoomed pan the image (single touch or mouse drag), clamped so
 * the image can never be dragged fully off-screen. At 1x, single-finger touches
 * fall through to onSwipe so existing slide navigation still works. The module
 * sets touch-action:none on the container — callers are full-screen modals, so
 * there is no page scroll to preserve.
 *
 * No dependencies, no CSS file: geometry is inline styles only. ES5.
 */

window.JRS = window.JRS || {};

(function (JRS) {
  'use strict';

  if (JRS.ImageZoom) return; // multiple sections may include this asset

  var REDUCED_MOTION = false;
  try {
    REDUCED_MOTION = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) { /* older browsers: keep animations */ }

  function attach(container, img, opts) {
    if (!container || !img) return null;
    opts = opts || {};
    var maxScale = opts.maxScale || 4;
    var doubleTapScale = opts.doubleTapScale || 2.5;

    // --- state ---------------------------------------------------------
    var scale = 1;
    var tx = 0, ty = 0;           // translation in px, applied before scale
    var baseRect = null;           // img rect at scale 1, relative sizing basis
    var zoomTypesFired = {};       // one onZoom per gesture type per attach
    var destroyed = false;

    // pinch bookkeeping
    var pinchStartDist = 0, pinchStartScale = 1, pinchStartTx = 0, pinchStartTy = 0;
    var pinchStartMid = null;
    var pinching = false;

    // pan bookkeeping (mouse + single-touch)
    var panning = false, panStartX = 0, panStartY = 0, panStartTx = 0, panStartTy = 0;
    var panMoved = false;

    // swipe-at-1x + double-tap bookkeeping
    var touchStartX = 0, touchStartY = 0, touchStartT = 0;
    var lastTapT = 0, lastTapX = 0, lastTapY = 0;

    var prevContainerTouchAction = container.style.touchAction;
    var prevContainerOverflow = container.style.overflow;
    var prevContainerPosition = container.style.position;
    var prevImgWillChange = img.style.willChange;
    container.style.touchAction = 'none';
    if (getComputedStyle(container).position === 'static') container.style.position = 'relative';
    // clip:false — caller has chrome overhanging the container box (e.g. the
    // bundle modal's close button at top:-40px) that overflow:hidden would
    // amputate. The zoomed image then overlaps the backdrop instead, which
    // reads as normal lightbox behaviour; the pan clamp still bounds it.
    if (opts.clip !== false) container.style.overflow = 'hidden';
    img.style.willChange = 'transform';
    img.style.transformOrigin = 'center center';

    function measure() {
      // Rect of the untransformed image. Divide out the current transform so a
      // refresh mid-zoom still yields the scale-1 footprint.
      var r = img.getBoundingClientRect();
      baseRect = { width: r.width / scale, height: r.height / scale };
    }

    function apply(animated) {
      img.style.transition = (animated && !REDUCED_MOTION) ? 'transform 0.25s ease' : 'none';
      img.style.transform = 'translate3d(' + tx + 'px,' + ty + 'px,0) scale(' + scale + ')';
      container.style.cursor = scale > 1.01 ? (panning ? 'grabbing' : 'grab') : '';
    }

    function clampScale(s) { return Math.min(maxScale, Math.max(1, s)); }

    // Keep the image edges pinned to the container: the visible overflow on
    // each axis is (scaled size - container size) / 2; translation may not
    // exceed it. When the scaled image is smaller than the container, centre.
    function clampPan() {
      if (!baseRect) measure();
      var maxX = Math.max(0, (baseRect.width * scale - container.clientWidth) / 2 + 24);
      var maxY = Math.max(0, (baseRect.height * scale - container.clientHeight) / 2 + 24);
      if (tx > maxX) tx = maxX; if (tx < -maxX) tx = -maxX;
      if (ty > maxY) ty = maxY; if (ty < -maxY) ty = -maxY;
      if (scale <= 1.01) { tx = 0; ty = 0; }
    }

    function fireZoom(type) {
      if (zoomTypesFired[type]) return;
      zoomTypesFired[type] = true;
      hideHint();
      if (typeof opts.onZoom === 'function') {
        try { opts.onZoom(type, scale); } catch (e) { /* analytics must never break UX */ }
      }
    }

    // Zoom so the container-point (cx, cy — relative to container centre)
    // keeps showing the same image point at the new scale.
    function zoomAnchored(newScale, cx, cy, animated) {
      newScale = clampScale(newScale);
      var k = newScale / scale;
      tx = cx - (cx - tx) * k;
      ty = cy - (cy - ty) * k;
      scale = newScale;
      clampPan();
      apply(animated);
    }

    function containerPoint(clientX, clientY) {
      var r = container.getBoundingClientRect();
      return {
        x: clientX - r.left - r.width / 2,
        y: clientY - r.top - r.height / 2
      };
    }

    function reset(animated) {
      scale = 1; tx = 0; ty = 0;
      apply(animated !== false);
    }

    function toggleDoubleZoom(clientX, clientY, type) {
      if (scale > 1.05) {
        reset(true);
      } else {
        var p = containerPoint(clientX, clientY);
        zoomAnchored(doubleTapScale, p.x, p.y, true);
        fireZoom(type);
      }
    }

    // --- hint pill -----------------------------------------------------
    var hintEl = null, hintTimer = null;
    function showHint() {
      if (!opts.hint) return;
      hintEl = document.createElement('div');
      hintEl.setAttribute('aria-hidden', 'true');
      hintEl.textContent = opts.hint;
      hintEl.style.cssText =
        'position:absolute;left:50%;bottom:16px;transform:translateX(-50%);' +
        'background:rgba(17,17,17,0.72);color:#fff;font-size:12.5px;line-height:1;' +
        'padding:8px 14px;border-radius:999px;pointer-events:none;z-index:5;' +
        'white-space:nowrap;transition:opacity 0.4s ease;';
      container.appendChild(hintEl);
      hintTimer = setTimeout(hideHint, 3200);
    }
    function hideHint() {
      if (hintTimer) { clearTimeout(hintTimer); hintTimer = null; }
      if (hintEl) {
        var el = hintEl; hintEl = null;
        el.style.opacity = '0';
        setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 450);
      }
    }

    // --- touch ---------------------------------------------------------
    function dist(t0, t1) {
      var dx = t0.clientX - t1.clientX, dy = t0.clientY - t1.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }

    function onTouchStart(e) {
      if (e.touches.length === 2) {
        pinching = true; panning = false;
        pinchStartDist = dist(e.touches[0], e.touches[1]);
        pinchStartScale = scale;
        pinchStartTx = tx; pinchStartTy = ty;
        pinchStartMid = containerPoint(
          (e.touches[0].clientX + e.touches[1].clientX) / 2,
          (e.touches[0].clientY + e.touches[1].clientY) / 2
        );
        e.preventDefault();
      } else if (e.touches.length === 1) {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        touchStartT = Date.now();
        if (scale > 1.01) {
          panning = true; panMoved = false;
          panStartX = touchStartX; panStartY = touchStartY;
          panStartTx = tx; panStartTy = ty;
          e.preventDefault();
        }
      }
    }

    function onTouchMove(e) {
      if (pinching && e.touches.length === 2) {
        e.preventDefault();
        var d = dist(e.touches[0], e.touches[1]);
        if (!pinchStartDist) return;
        var newScale = clampScale(pinchStartScale * (d / pinchStartDist));
        var mid = containerPoint(
          (e.touches[0].clientX + e.touches[1].clientX) / 2,
          (e.touches[0].clientY + e.touches[1].clientY) / 2
        );
        // anchor the image point that was under the start-midpoint to the
        // current midpoint (pinch + two-finger drag in one formula)
        var k = newScale / pinchStartScale;
        tx = mid.x - (pinchStartMid.x - pinchStartTx) * k;
        ty = mid.y - (pinchStartMid.y - pinchStartTy) * k;
        scale = newScale;
        clampPan();
        apply(false);
        if (scale > 1.01) fireZoom('pinch');
      } else if (panning && e.touches.length === 1) {
        e.preventDefault();
        var mx = e.touches[0].clientX - panStartX;
        var my = e.touches[0].clientY - panStartY;
        if (Math.abs(mx) > 3 || Math.abs(my) > 3) panMoved = true;
        tx = panStartTx + mx;
        ty = panStartTy + my;
        clampPan();
        apply(false);
      }
    }

    function onTouchEnd(e) {
      if (pinching) {
        if (e.touches.length < 2) pinching = false;
        // snap back if pinched below 1x
        if (!pinching && scale <= 1.01) reset(true);
        return;
      }
      if (e.touches.length > 0) return; // fingers remain

      var dt = Date.now() - touchStartT;
      var wasPan = panning; panning = false;
      var endX = (e.changedTouches && e.changedTouches[0]) ? e.changedTouches[0].clientX : touchStartX;
      var endY = (e.changedTouches && e.changedTouches[0]) ? e.changedTouches[0].clientY : touchStartY;
      var moved = Math.abs(endX - touchStartX) > 12 || Math.abs(endY - touchStartY) > 12;

      // double-tap: two quick, close, movement-free taps
      if (!moved && dt < 350) {
        var now = Date.now();
        if (now - lastTapT < 320 && Math.abs(endX - lastTapX) < 40 && Math.abs(endY - lastTapY) < 40) {
          lastTapT = 0;
          toggleDoubleZoom(endX, endY, 'double_tap');
          if (e.cancelable) e.preventDefault();
          return;
        }
        lastTapT = now; lastTapX = endX; lastTapY = endY;
      }

      // swipe-at-1x: hand horizontal flicks back to the caller (slide nav)
      if (!wasPan && scale <= 1.01 && typeof opts.onSwipe === 'function') {
        var dx = endX - touchStartX;
        var dy = endY - touchStartY;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.4) {
          opts.onSwipe(dx > 0 ? 'right' : 'left');
        }
      }
      apply(false);
    }

    // --- mouse ---------------------------------------------------------
    function onDblClick(e) {
      e.preventDefault();
      toggleDoubleZoom(e.clientX, e.clientY, 'double_click');
    }

    function onWheel(e) {
      e.preventDefault();
      var p = containerPoint(e.clientX, e.clientY);
      var factor = Math.exp(-e.deltaY * 0.0022);
      var before = scale;
      zoomAnchored(scale * factor, p.x, p.y, false);
      if (before <= 1.01 && scale > 1.01) fireZoom('wheel');
    }

    function onMouseDown(e) {
      if (scale <= 1.01 || e.button !== 0) return;
      e.preventDefault();
      panning = true; panMoved = false;
      panStartX = e.clientX; panStartY = e.clientY;
      panStartTx = tx; panStartTy = ty;
      apply(false);
    }
    function onMouseMove(e) {
      if (!panning) return;
      e.preventDefault();
      var mx = e.clientX - panStartX, my = e.clientY - panStartY;
      if (Math.abs(mx) > 3 || Math.abs(my) > 3) panMoved = true;
      tx = panStartTx + mx;
      ty = panStartTy + my;
      clampPan();
      apply(false);
    }
    function onMouseUp() {
      if (!panning) return;
      panning = false;
      apply(false);
    }

    // A drag-pan must not fall through as a "click" (lightboxes close or
    // navigate on click). Swallow the click that ends a real pan.
    function onClickCapture(e) {
      if (panMoved) {
        panMoved = false;
        e.stopPropagation();
        e.preventDefault();
      }
    }

    function onImgLoad() { scale = 1; tx = 0; ty = 0; baseRect = null; measure(); apply(false); }
    function onResize() { baseRect = null; measure(); clampPan(); apply(false); }

    container.addEventListener('touchstart', onTouchStart, { passive: false });
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd, { passive: false });
    container.addEventListener('dblclick', onDblClick);
    container.addEventListener('wheel', onWheel, { passive: false });
    container.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    container.addEventListener('click', onClickCapture, true);
    img.addEventListener('load', onImgLoad);
    window.addEventListener('resize', onResize);

    if (img.complete && img.naturalWidth) measure();
    showHint();

    return {
      isZoomed: function () { return scale > 1.01; },
      getScale: function () { return scale; },
      reset: function () { reset(true); },
      refresh: onImgLoad,
      destroy: function () {
        if (destroyed) return;
        destroyed = true;
        hideHint();
        container.removeEventListener('touchstart', onTouchStart);
        container.removeEventListener('touchmove', onTouchMove);
        container.removeEventListener('touchend', onTouchEnd);
        container.removeEventListener('dblclick', onDblClick);
        container.removeEventListener('wheel', onWheel);
        container.removeEventListener('mousedown', onMouseDown);
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
        container.removeEventListener('click', onClickCapture, true);
        img.removeEventListener('load', onImgLoad);
        window.removeEventListener('resize', onResize);
        img.style.transition = 'none';
        img.style.transform = '';
        img.style.willChange = prevImgWillChange;
        container.style.touchAction = prevContainerTouchAction;
        container.style.overflow = prevContainerOverflow;
        container.style.position = prevContainerPosition;
        container.style.cursor = '';
      }
    };
  }

  /** Default hint per input type: pinch copy on touch devices, wheel copy otherwise. */
  function defaultHint() {
    var touch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
    return touch ? 'Pinch or double-tap to zoom' : 'Double-click or scroll to zoom';
  }

  JRS.ImageZoom = { attach: attach, defaultHint: defaultHint };
})(window.JRS);
